import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { enableViewFavoriting, enableViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getSelectedViewQuery, usePutSelectedView } from '@gonfalon/rest-api/queries/profile';
import { useQuery } from '@tanstack/react-query';

import type { View } from '../../types';

const SELECTED_VIEW_PREFERENCES_STALE_TIME = 1000 * 60 * 5;

function emptyFavoritePreferences() {
  return {
    selectedViewKey: '',
    favorites: [] as string[],
  };
}

function pinSelectedViewToTop(views: View[], selectedViewKey?: string): View[] {
  if (!selectedViewKey) {
    return views;
  }

  const selectedIndex = views.findIndex((view) => view.key === selectedViewKey);
  if (selectedIndex <= 0) {
    return views;
  }

  const selectedView = views[selectedIndex];
  return [selectedView, ...views.slice(0, selectedIndex), ...views.slice(selectedIndex + 1)];
}

interface UseFavoriteViewsOptions {
  projectKey: string;
  allViews: View[];
  selectedViews: View[];
  selectionMode: 'single' | 'multiple';
  isFiltered: boolean;
}

interface UseFavoriteViewsReturn {
  isViewFavoritingEnabled: boolean;
  canToggleFavorites: boolean;
  shouldRenderSections: boolean;
  favoriteViewKeys: Set<string>;
  orderedFavoriteViews: View[];
  orderedAllNonFavoriteViews: View[];
  handleFavoriteToggle: (viewKey: string) => void;
}

export function useFavoriteViews({
  projectKey,
  allViews,
  selectedViews,
  selectionMode,
  isFiltered,
}: UseFavoriteViewsOptions): UseFavoriteViewsReturn {
  const isViewFavoritingEnabled = enableViews() && enableViewFavoriting();
  const { data: selectedViewPreferences } = useQuery({
    ...getSelectedViewQuery({ projKey: projectKey }),
    staleTime: SELECTED_VIEW_PREFERENCES_STALE_TIME,
    enabled: isViewFavoritingEnabled,
  });
  const { mutate: updateSelectedViewPreferences } = usePutSelectedView();
  const [optimisticFavorites, setOptimisticFavorites] = useState<string[] | null>(null);

  // Ref used by handleFavoriteToggle to read the latest snapshot without waiting
  // for a React re-render, which is critical for rapid consecutive toggles.
  const preferencesRef = useRef<{ selectedViewKey: string; favorites: string[] }>({
    selectedViewKey: '',
    favorites: [],
  });

  const [favoritesScope, setFavoritesScope] = useState({ projectKey, isViewFavoritingEnabled });
  if (favoritesScope.projectKey !== projectKey || favoritesScope.isViewFavoritingEnabled !== isViewFavoritingEnabled) {
    setFavoritesScope({ projectKey, isViewFavoritingEnabled });
    setOptimisticFavorites(null);
  }

  const [prevSelectedViewPreferences, setPrevSelectedViewPreferences] = useState(selectedViewPreferences);
  if (prevSelectedViewPreferences !== selectedViewPreferences) {
    setPrevSelectedViewPreferences(selectedViewPreferences);
    if (selectedViewPreferences !== undefined) {
      setOptimisticFavorites(selectedViewPreferences.favorites ?? []);
    }
  }

  useEffect(() => {
    if (
      favoritesScope.projectKey === projectKey &&
      favoritesScope.isViewFavoritingEnabled === isViewFavoritingEnabled
    ) {
      preferencesRef.current = emptyFavoritePreferences();
    }
  }, [favoritesScope, projectKey, isViewFavoritingEnabled]);

  useEffect(() => {
    if (selectedViewPreferences !== undefined) {
      preferencesRef.current = {
        selectedViewKey: selectedViewPreferences.selectedViewKey ?? '',
        favorites: selectedViewPreferences.favorites ?? [],
      };
    }
  }, [selectedViewPreferences]);

  const favorites = optimisticFavorites ?? selectedViewPreferences?.favorites ?? [];
  const favoriteViewKeys = useMemo(() => new Set(favorites), [favorites]);

  const favoriteViews = useMemo(
    () => allViews.filter((view) => favoriteViewKeys.has(view.key)),
    [allViews, favoriteViewKeys],
  );
  const allNonFavoriteViews = useMemo(
    () => allViews.filter((view) => !favoriteViewKeys.has(view.key)),
    [allViews, favoriteViewKeys],
  );

  // Pin the view that was selected when the dialog opened — a mount-time snapshot, not the live
  // selection. Re-sorting while the menu is open moves items out from under keyboard and
  // screen-reader users (REL-15082); selection feedback while open is the in-place checkmark.
  // The dialog remounts on each popover open, so the pin refreshes on the next open.
  const [pinnedViewKey] = useState(() => selectedViews[0]?.key);
  const orderedFavoriteViews = useMemo(
    () => pinSelectedViewToTop(favoriteViews, pinnedViewKey),
    [favoriteViews, pinnedViewKey],
  );
  const orderedAllNonFavoriteViews = useMemo(
    () => pinSelectedViewToTop(allNonFavoriteViews, pinnedViewKey),
    [allNonFavoriteViews, pinnedViewKey],
  );

  const shouldRenderSections =
    isViewFavoritingEnabled && !isFiltered && favoriteViews.length > 0 && allNonFavoriteViews.length > 0;
  const canToggleFavorites =
    isViewFavoritingEnabled && selectionMode === 'single' && selectedViewPreferences !== undefined;

  const handleFavoriteToggle = useCallback(
    (viewKey: string) => {
      if (!viewKey || !canToggleFavorites) {
        return;
      }

      const { selectedViewKey: currentSelectedViewKey, favorites: currentFavorites } = preferencesRef.current;
      const isFavorite = currentFavorites.includes(viewKey);
      const nextFavorites = isFavorite
        ? currentFavorites.filter((fav) => fav !== viewKey)
        : [...new Set([...currentFavorites, viewKey])];

      preferencesRef.current = {
        selectedViewKey: currentSelectedViewKey,
        favorites: nextFavorites,
      };
      setOptimisticFavorites(nextFavorites);

      updateSelectedViewPreferences({
        projKey: projectKey,
        body: {
          selectedViewKey: currentSelectedViewKey,
          favorites: nextFavorites,
        },
      });
    },
    [canToggleFavorites, projectKey, updateSelectedViewPreferences],
  );

  return {
    isViewFavoritingEnabled,
    canToggleFavorites,
    shouldRenderSections,
    favoriteViewKeys,
    orderedFavoriteViews,
    orderedAllNonFavoriteViews,
    handleFavoriteToggle,
  };
}
